import React from 'react';
import { CheckCircle, Circle, Clock, User, Send, FileCheck } from 'lucide-react';

interface TaskWorkflowProgressProps {
  status: string;
  assignees?: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  currentUserId: string;
}

interface WorkflowStep {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  status: 'completed' | 'active' | 'pending';
  isUserStep: boolean;
}

export const TaskWorkflowProgress: React.FC<TaskWorkflowProgressProps> = ({
  status,
  assignees,
  currentUserId,
}) => {
  const getWorkflowSteps = (): WorkflowStep[] => {
    const steps: WorkflowStep[] = [
      {
        id: 'prepare',
        label: 'Prepare',
        icon: User,
        status: 'pending',
        isUserStep: assignees?.preparerId === currentUserId,
      },
      {
        id: 'review',
        label: 'Review',
        icon: FileCheck,
        status: 'pending',
        isUserStep: assignees?.reviewerId === currentUserId,
      },
      {
        id: 'submit',
        label: 'Submit',
        icon: Send,
        status: 'pending',
        isUserStep: assignees?.submitterId === currentUserId,
      },
      {
        id: 'complete',
        label: 'Complete',
        icon: CheckCircle,
        status: 'pending',
        isUserStep: false,
      },
    ];

    // Update step statuses based on current task status
    switch (status) {
      case 'draft':
        steps[0].status = 'active';
        break;
      case 'in_progress':
        steps[0].status = 'active';
        break;
      case 'in_review':
        steps[0].status = 'completed';
        steps[1].status = 'active';
        break;
      case 'ready_to_submit':
        steps[0].status = 'completed';
        steps[1].status = 'completed';
        steps[2].status = 'active';
        break;
      case 'submitted':
        steps[0].status = 'completed';
        steps[1].status = 'completed';
        steps[2].status = 'completed';
        steps[3].status = 'active';
        break;
      case 'completed':
        steps.forEach(step => step.status = 'completed');
        break;
      case 'blocked':
        steps[0].status = 'active';
        break;
    }

    return steps;
  };

  const steps = getWorkflowSteps();

  const getStepColor = (step: WorkflowStep) => {
    if (step.status === 'completed') {
      return 'text-green-600 bg-green-100';
    } else if (step.status === 'active') {
      return step.isUserStep 
        ? 'text-blue-600 bg-blue-100 ring-2 ring-blue-200' 
        : 'text-yellow-600 bg-yellow-100';
    } else {
      return 'text-gray-400 bg-gray-100';
    }
  };

  const getConnectorColor = (index: number) => {
    const currentStep = steps[index];
    const nextStep = steps[index + 1];
    
    if (currentStep.status === 'completed') {
      return 'bg-green-300';
    } else {
      return 'bg-gray-300';
    }
  };

  return (
    <div className="flex items-center space-x-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`
                  flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200
                  ${getStepColor(step)}
                  ${step.isUserStep && step.status === 'active' ? 'animate-pulse' : ''}
                `}
                title={`${step.label}${step.isUserStep ? ' (Your Step)' : ''}`}
              >
                <Icon className="w-4 h-4" />
              </div>
              <span className="text-xs text-gray-600 mt-1 font-medium">
                {step.label}
              </span>
              {step.isUserStep && step.status === 'active' && (
                <span className="text-xs text-blue-600 font-semibold">
                  Your Turn
                </span>
              )}
            </div>
            
            {/* Connector line */}
            {index < steps.length - 1 && (
              <div className="flex-1 h-0.5 mx-2 transition-colors duration-200">
                <div className={`h-full ${getConnectorColor(index)}`} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Compact version for use in cards
export const TaskWorkflowProgressCompact: React.FC<TaskWorkflowProgressProps> = ({
  status,
  assignees,
  currentUserId,
}) => {
  const getWorkflowSteps = (): WorkflowStep[] => {
    const steps: WorkflowStep[] = [
      {
        id: 'prepare',
        label: 'P',
        icon: User,
        status: 'pending',
        isUserStep: assignees?.preparerId === currentUserId,
      },
      {
        id: 'review',
        label: 'R',
        icon: FileCheck,
        status: 'pending',
        isUserStep: assignees?.reviewerId === currentUserId,
      },
      {
        id: 'submit',
        label: 'S',
        icon: Send,
        status: 'pending',
        isUserStep: assignees?.submitterId === currentUserId,
      },
      {
        id: 'complete',
        label: 'C',
        icon: CheckCircle,
        status: 'pending',
        isUserStep: false,
      },
    ];

    // Update step statuses based on current task status
    switch (status) {
      case 'draft':
        steps[0].status = 'active';
        break;
      case 'in_progress':
        steps[0].status = 'active';
        break;
      case 'in_review':
        steps[0].status = 'completed';
        steps[1].status = 'active';
        break;
      case 'ready_to_submit':
        steps[0].status = 'completed';
        steps[1].status = 'completed';
        steps[2].status = 'active';
        break;
      case 'submitted':
        steps[0].status = 'completed';
        steps[1].status = 'completed';
        steps[2].status = 'completed';
        steps[3].status = 'active';
        break;
      case 'completed':
        steps.forEach(step => step.status = 'completed');
        break;
      case 'blocked':
        steps[0].status = 'active';
        break;
    }

    return steps;
  };

  const steps = getWorkflowSteps();

  const getStepColor = (step: WorkflowStep) => {
    if (step.status === 'completed') {
      return 'text-green-600 bg-green-100';
    } else if (step.status === 'active') {
      return step.isUserStep 
        ? 'text-blue-600 bg-blue-100 ring-1 ring-blue-200' 
        : 'text-yellow-600 bg-yellow-100';
    } else {
      return 'text-gray-400 bg-gray-100';
    }
  };

  return (
    <div className="flex items-center space-x-1">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div
            className={`
              flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all duration-200
              ${getStepColor(step)}
              ${step.isUserStep && step.status === 'active' ? 'animate-pulse' : ''}
            `}
            title={`${step.id.charAt(0).toUpperCase() + step.id.slice(1)}${step.isUserStep ? ' (Your Step)' : ''}`}
          >
            {step.label}
          </div>
          
          {/* Connector dot */}
          {index < steps.length - 1 && (
            <div className="w-1 h-1 bg-gray-300 rounded-full" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Status indicator with next action
export const TaskWorkflowStatus: React.FC<TaskWorkflowProgressProps & {
  onAction?: (action: string) => void;
}> = ({ status, assignees, currentUserId, onAction }) => {


  // Enhanced user role detection - extract IDs from user objects or serialized strings
  const getIdFromAssignee = (assignee: any): string | null => {
    if (!assignee) return null;

    // If it's a string that looks like a serialized object, extract the ObjectId
    if (typeof assignee === 'string') {
      // Check if it's a serialized object string
      if (assignee.includes('ObjectId(')) {
        // Extract the ObjectId value using regex
        const match = assignee.match(/ObjectId\('([^']+)'\)/);
        if (match && match[1]) {
          return match[1];
        }
      }
      // If it's just a plain string ID, return it
      return assignee;
    }

    // If it's a user object with _id, extract the _id
    if (assignee._id) return assignee._id.toString();

    // If it's an ObjectId, convert to string
    return assignee.toString();
  };

  const preparerIdStr = getIdFromAssignee(assignees?.preparerId);
  const reviewerIdStr = getIdFromAssignee(assignees?.reviewerId);
  const submitterIdStr = getIdFromAssignee(assignees?.submitterId);
  const currentUserIdStr = currentUserId?.toString();



  const userRole = preparerIdStr === currentUserIdStr ? 'preparer' :
                   reviewerIdStr === currentUserIdStr ? 'reviewer' :
                   submitterIdStr === currentUserIdStr ? 'submitter' : null;

  const getStatusInfo = () => {
    switch (status) {
      case 'draft':
        return {
          message: userRole === 'preparer' ? 'Ready to start work' : 'Waiting for preparer to start',
          action: userRole === 'preparer' ? { label: 'Start Work', action: 'start-work' } : null,
          color: 'text-blue-600 bg-blue-50',
        };
      case 'in_progress':
        return {
          message: userRole === 'preparer' ? 'Work in progress' : 'Preparer is working on this task',
          action: userRole === 'preparer' ? { label: 'Submit for Review', action: 'submit-for-review' } : null,
          color: 'text-yellow-600 bg-yellow-50',
        };
      case 'in_review':
        return {
          message: userRole === 'reviewer' ? 'Ready for your review' : 'Under review',
          action: userRole === 'reviewer' ? { label: 'Complete Review', action: 'complete-review' } : null,
          secondaryAction: userRole === 'reviewer' ? { label: 'Return to Preparer', action: 'return-to-preparer' } : null,
          color: 'text-purple-600 bg-purple-50',
        };
      case 'ready_to_submit':
        return {
          message: userRole === 'submitter' ? 'Ready for submission' : 'Waiting for submission',
          action: userRole === 'submitter' ? { label: 'Submit', action: 'submit-task' } : null,
          secondaryAction: userRole === 'submitter' ? { label: 'Return to Reviewer', action: 'return-to-reviewer' } : null,
          color: 'text-blue-600 bg-blue-50',
        };
      case 'submitted':
        return {
          message: 'Task submitted - Invoice generated',
          action: null,
          color: 'text-green-600 bg-green-50',
        };
      case 'completed':
        return {
          message: 'Task completed',
          action: null,
          color: 'text-green-600 bg-green-50',
        };
      case 'blocked':
        return {
          message: 'Task is blocked',
          action: userRole === 'preparer' ? { label: 'Resume Work', action: 'start-work' } : null,
          color: 'text-red-600 bg-red-50',
        };
      default:
        return {
          message: 'Unknown status',
          action: null,
          color: 'text-gray-600 bg-gray-50',
        };
    }
  };

  const statusInfo = getStatusInfo();



  return (
    <div className={`p-3 rounded-lg ${statusInfo.color}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{statusInfo.message}</span>
        <div className="flex items-center space-x-2">
          {statusInfo.action && onAction && (
            <button
              onClick={() => onAction(statusInfo.action!.action)}
              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              {statusInfo.action.label}
            </button>
          )}
          {statusInfo.secondaryAction && onAction && (
            <button
              onClick={() => onAction(statusInfo.secondaryAction!.action)}
              className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700"
            >
              {statusInfo.secondaryAction.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
